import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { FormGroup, Col, Button, Alert } from 'reactstrap';
import { Link } from 'react-router-dom';

import Segment from '../components/Segment';
import SegmentItem from '../components/SegmentItem';

import {
  saveSource,
  testConnection,
  clearSourceMessage,
  fetchAllSources,
} from '../../../../../actions/synchronisationActions';
import { InputField, NativeSelect, RHFField, rfRules } from '../../../../../components/Field';
import { required } from '../../../../../components/Field/validate';
import DbAliasSelect from './DbAliasSelect';
import DbSchemaSelect from './DbSchemaSelect';
import SyncVersionField from './SyncVersionField';
import { getStore as getGatewayStore } from '../../../../../actions/gatewayActions';
import { API_GATEWAY_INTEGRATION_BUSINESS_CENTRAL } from '../../../../../utils/constants';

const SYNC_VERSION_OPTIONS = [
  { name: 'Off (None)', value: 'NONE' },
  { name: 'Sync 1', value: 'SYNC_1' },
  { name: 'Sync 2', value: 'SYNC_2' },
];

function equalsIgnoreCase(a, b) {
  return typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase();
}

// Connections that point at the same Database id on the same agent as `current`. They share one
// configuration namespace on the agent, so either one's "Send Mappings" clears the columns the
// other added — including when their Database schemas differ (PDECOM-6188).
//
// This deliberately includes other connections of the *same* store. They collide in exactly the
// same way, and that case became reachable once Send Mappings could target a connection other
// than the store's primary.
export function findSharedDbAliasSources(allSources, current) {
  if (!Array.isArray(allSources) || !current.dbAlias || !current.hostName || !current.port) {
    return [];
  }
  return allSources.filter(
    (source) =>
      source.id !== current.id &&
      equalsIgnoreCase(source.dbAlias, current.dbAlias) &&
      equalsIgnoreCase(source.hostName, current.hostName) &&
      String(source.port) === String(current.port)
  );
}

const GATEWAY_SYNC_SOURCES = ['MS_Dynamics_BC'];
const GATEWAY_SYNC_TYPE = 'GATEWAY';
const AGENT_SYNC_TYPE = 'AGENT';

export function getSyncVersionName(value) {
  let syncVersionName = '';
  SYNC_VERSION_OPTIONS.forEach((option) => {
    if (option.value != value) {
      return '';
    }
    syncVersionName = option.name;
  });

  return syncVersionName;
}

const DB_TYPE_OPTIONS = [
  { name: 'mssql', value: 'MSSQL' },
  { name: 'oracle', value: 'ORACLE' },
];

const PROTOCOL_TYPE_OPTIONS = [
  { name: 'https', value: 'https' },
  { name: 'http', value: 'http' },
];

const SOURCE_TYPE_OPTIONS = [
  {
    name: 'Sage X3 V6.5',
    value: 'SAGE_X3_V6_5',
    code: 'SAGE_X3_V6_5',
    isGatewaySyncAvailable: false,
  },
  {
    name: 'Sage X3 V7',
    value: 'SAGE_X3_V7',
    code: 'SAGE_X3_V7',
    isGatewaySyncAvailable: false,
  },
  {
    name: 'Sage X3 V8',
    value: 'SAGE_X3_V8',
    code: 'SAGE_X3_V8',
    isGatewaySyncAvailable: false,
  },
  {
    name: 'Sage X3 V9',
    value: 'SAGE_X3_V9',
    code: 'SAGE_X3_V9',
    isGatewaySyncAvailable: false,
  },
  {
    name: 'Sage X3 V10',
    value: 'SAGE_X3_V10',
    code: 'SAGE_X3_V10',
    isGatewaySyncAvailable: false,
  },
  {
    name: 'Sage X3 V11',
    value: 'SAGE_X3_V11',
    code: 'SAGE_X3_V11',
    isGatewaySyncAvailable: false,
  },
  {
    name: 'Sage X3 V12',
    value: 'SAGE_X3_V12',
    code: 'SAGE_X3_V12',
    isGatewaySyncAvailable: false,
  },
  {
    name: 'Sage 300',
    value: 'SAGE_300',
    code: 'SAGE_300',
    isGatewaySyncAvailable: false,
  },
  {
    name: 'Microsoft Dynamics 365 Business Central (Wave 1 2021)',
    value: 'BUSINESSCENTRAL_V18_5',
    code: 'BUSINESSCENTRAL_V18_5',
    isGatewaySyncAvailable: true,
  },
];

const erpTypeSources = SOURCE_TYPE_OPTIONS;

// Keeps every field controlled across reinitialize (native <select> warns if its
// value flips between undefined and a string).
const EMPTY_DEFAULTS = {
  storeId: '',
  syncVersion: '',
  sourceType: '',
  syncType: '',
  integrationName: '',
  dbType: '',
  protocol: '',
  hostName: '',
  port: '',
  dbAlias: '',
  dbSchema: '',
};

// Migrated from redux-form to react-hook-form. SyncForm and its children
// (SyncVersionField / DbAliasSelect / DbSchemaSelect) share one form: the children
// receive `control` and register their fields (syncVersion / dbAlias / dbSchema)
// via RHFField, replacing the shared redux-form 'syncForm' context. redux-form
// change() -> setValue(); the protocol/hostName/port/dbAlias the children need are
// read live off watch(); the sourceType cascade (set syncType + maybe fetch the
// gateway store) runs off a watch effect so it fires on both load and user change,
// matching the old onChange + initialValues behaviour. withRouter was unused -> dropped.
function SyncForm({ currentStore, initialValues }) {
  const dispatch = useDispatch();
  const failed = useSelector((s) => s.sync.source.failed);
  const message = useSelector((s) => s.sync.source.message);
  const failedSyncVersion = useSelector((s) => s.sync.source.failedSyncVersion);
  const messageSyncVersion = useSelector((s) => s.sync.source.messageSyncVersion);
  const loading = useSelector((s) => s.sync.source.loading);
  const resetSyncVersionSignal = useSelector((s) => s.sync.source.resetSyncVersionSignal);
  const currentGatewayStore = useSelector((s) => s.gateway.one);

  const { control, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: { ...EMPTY_DEFAULTS, ...initialValues },
  });

  // initialValues comes from getSource() which returns a fresh object every render;
  // serialize so we reinitialize only when the values actually change (enableReinitialize).
  const initKey = JSON.stringify(initialValues);
  useEffect(() => {
    reset({ ...EMPTY_DEFAULTS, ...initialValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initKey]);

  // Revert the syncVersion select to the store's authoritative value when a
  // version change is cancelled or fails (replaces redux-form's reset('syncForm')).
  useEffect(() => {
    setValue('syncVersion', currentStore.syncVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSyncVersionSignal]);

  const sourceType = watch('sourceType');
  const protocol = watch('protocol');
  const hostName = watch('hostName');
  const port = watch('port');
  const dbAlias = watch('dbAlias');

  const selectedErpSource = erpTypeSources.find((type) => type.value === sourceType) || null;

  // sourceType cascade: set the sync type and (for gateway sources) fetch the gateway store.
  // Fires on initial reinitialize and on user change, mirroring the old behaviour.
  useEffect(() => {
    if (!selectedErpSource) return;
    setValue('syncType', selectedErpSource.isGatewaySyncAvailable ? GATEWAY_SYNC_TYPE : AGENT_SYNC_TYPE);
    if (GATEWAY_SYNC_SOURCES.includes(selectedErpSource.code)) {
      dispatch(getGatewayStore(currentStore.storeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType]);

  function submit(values) {
    dispatch(saveSource(currentStore.storeId, values));
  }

  function handleDbAliasSelect(value) {
    setValue('dbAlias', value);
  }

  // Two connections pointed at the same Database id on the same agent share one metadata
  // namespace, so either one's "Send Mappings" clears the columns the other added (PDECOM-6188).
  // Nothing stops that configuration being created, so warn when it is about to be.
  const [allSources, setAllSources] = useState(null);
  const [showSharedDbAlias, setShowSharedDbAlias] = useState(false);
  useEffect(() => {
    let current = true;
    dispatch(fetchAllSources()).then((sources) => {
      if (current) setAllSources(sources);
    });
    return () => {
      current = false;
    };
  }, [dispatch]);

  const sharedDbAliasStores = useMemo(
    () =>
      findSharedDbAliasSources(allSources, {
        id: initialValues.id,
        storeId: currentStore.storeId,
        dbAlias,
        hostName,
        port,
      }),
    [allSources, dbAlias, hostName, port, initialValues.id, currentStore.storeId]
  );

  function handleDbSchemaSelect(value) {
    setValue('dbSchema', value);
  }

  function handleTestConnection() {
    dispatch(testConnection(currentStore.storeId, protocol, hostName, port));
  }

  function isSync2Mode() {
    return currentStore.syncVersion == 'SYNC_2';
  }

  function onDismiss() {
    dispatch(clearSourceMessage());
  }

  const clearSyncVersionMessage = () => dispatch({ type: 'SYNC_CLEAR_VERSION_MESSAGE' });

  const isSourceSelected = () => {
    return selectedErpSource !== null;
  };

  const isGatewaySync = () => {
    return (isSourceSelected() && selectedErpSource.isGatewaySyncAvailable) || false;
  };

  const hasGatewayStore = () => {
    return currentGatewayStore !== null && currentGatewayStore.storeId !== null;
  };

  const isGatewayStoreValid = () => {
    return hasGatewayStore() && currentGatewayStore.enabled == true && currentGatewayStore.integrations.length > 0;
  };

  const isBusinessCentral = () => {
    return currentStore && currentStore.erpType && currentStore.erpType.code === 'MS_Dynamics_BC';
  };

  if (!currentStore.id) {
    return <div>Loading...</div>;
  }

  return (
    <form className="form-horizontal">
      <FormGroup row>
        <Col sm={3}>Store ID</Col>
        <Col sm={9}>
          <RHFField
            name="storeId"
            control={control}
            component={InputField}
            className="px-1"
            readOnly="readonly"
            type="text"
            id="storeId"
            rules={rfRules([required])}
          />
        </Col>
      </FormGroup>

      {messageSyncVersion && (
        <div className="mt-4">
          <Alert
            color={failedSyncVersion ? 'danger' : 'success'}
            isOpen={!!messageSyncVersion}
            toggle={clearSyncVersionMessage}
          >
            {messageSyncVersion}
          </Alert>
        </div>
      )}

      <Segment title="Sync Version">
        <SegmentItem name="Version" style={{ marginTop: '-1em' }}>
          <FormGroup className="col-sm-12 col-xs-12 col-md-12 col-lg-12" style={{ marginBottom: 'none' }}>
            <SyncVersionField
              control={control}
              currentStore={currentStore}
              initialValues={initialValues}
              options={SYNC_VERSION_OPTIONS}
              disabled={loading || isBusinessCentral()}
            />
          </FormGroup>
        </SegmentItem>
      </Segment>

      {isSync2Mode() && (
        <Segment title="ERP">
          <SegmentItem name="Type">
            <FormGroup className="col-sm-12 col-xs-12 col-md-12 col-lg-12" style={{ marginBottom: 'none' }}>
              <RHFField
                name="sourceType"
                control={control}
                component={NativeSelect}
                type="select"
                id="sourceType"
                rules={rfRules([required])}
                options={erpTypeSources}
                disabled={isBusinessCentral()}
              />
            </FormGroup>
          </SegmentItem>
        </Segment>
      )}

      {isSync2Mode() && isSourceSelected() && isGatewaySync() && (
        <Segment title="Sync 2 - Gateway">
          {!isGatewayStoreValid() && (
            <SegmentItem name="Settings">
              <Link to={`/stores/${currentStore.id}/gateway`}>Gateway Settings</Link>
            </SegmentItem>
          )}
          {hasGatewayStore() && (
            <div
              className="row form-group"
              style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}
            >
              <Button color="success" disabled={true}>
                Live
              </Button>
              <Button color={currentGatewayStore.enabled ? 'success' : 'secondary'} disabled={true}>
                {currentGatewayStore.enabled ? 'Enabled' : 'Disabled'}
              </Button>
            </div>
          )}

          {hasGatewayStore() && (
            <SegmentItem name="Sync Type">
              <FormGroup className="col-sm-12 col-xs-12 col-md-6 col-lg-6" style={{ marginBottom: 'none' }}>
                <RHFField
                  name="syncType"
                  control={control}
                  component={InputField}
                  className="px-1"
                  readOnly="readonly"
                  type="text"
                  id="syncType"
                  rules={rfRules([required])}
                  placeholder={GATEWAY_SYNC_TYPE}
                />
              </FormGroup>
            </SegmentItem>
          )}
          {hasGatewayStore() && (
            <SegmentItem name="Integration">
              <FormGroup className="col-sm-12 col-xs-12 col-md-12 col-lg-12" style={{ marginBottom: 'none' }}>
                <RHFField
                  name="integrationName"
                  control={control}
                  component={NativeSelect}
                  type="select"
                  id="integrationName"
                  rules={rfRules([required])}
                  disabled={isBusinessCentral()}
                  options={
                    isBusinessCentral()
                      ? [
                          {
                            name: API_GATEWAY_INTEGRATION_BUSINESS_CENTRAL,
                            value: API_GATEWAY_INTEGRATION_BUSINESS_CENTRAL,
                          },
                        ]
                      : currentGatewayStore.integrations.map((integration) => {
                          return {
                            name: integration,
                            value: integration,
                          };
                        })
                  }
                />
              </FormGroup>
            </SegmentItem>
          )}
        </Segment>
      )}

      {isSync2Mode() && isSourceSelected() && !isGatewaySync() && (
        <Segment title="Sync 2 - Agent">
          <SegmentItem name="Database">
            <FormGroup className="col-sm-12 col-xs-12 col-md-12 col-lg-12" style={{ marginBottom: 'none' }}>
              <RHFField
                name="dbType"
                control={control}
                component={NativeSelect}
                type="select"
                id="dbType"
                options={DB_TYPE_OPTIONS}
                rules={rfRules([required])}
              />
            </FormGroup>
          </SegmentItem>
          <SegmentItem name="Connection">
            <FormGroup className="col-sm-12 col-xs-12 col-md-3 col-lg-3" style={{ marginBottom: 'none' }}>
              <RHFField
                name="protocol"
                control={control}
                component={NativeSelect}
                id="protocol"
                options={PROTOCOL_TYPE_OPTIONS}
                rules={rfRules([required])}
              />
            </FormGroup>
            <FormGroup className="col-sm-12 col-xs-12 col-md-6 col-lg-6" style={{ marginBottom: 'none' }}>
              <RHFField
                name="hostName"
                control={control}
                component={InputField}
                type="text"
                id="hostName"
                placeholder="host name"
                rules={rfRules([required])}
              />
            </FormGroup>
            <FormGroup className="col-sm-12 col-xs-12 col-md-3 col-lg-3" style={{ marginBottom: 'none' }}>
              <RHFField
                name="port"
                control={control}
                component={InputField}
                type="text"
                id="port"
                rules={rfRules([required])}
              />
            </FormGroup>
          </SegmentItem>

          <SegmentItem name="Database id">
            <DbAliasSelect
              control={control}
              storeId={currentStore.storeId}
              protocol={protocol}
              hostName={hostName}
              port={port}
              onDatabaseIDSelect={handleDbAliasSelect}
            />
          </SegmentItem>

          {/* Full-width row under the Database id field. SegmentItem lays its children out in a
              flex row, so the warning cannot live inside it — it would sit beside the field in
              whatever width was left over. The empty label cell keeps it aligned with the input. */}
          {sharedDbAliasStores.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <span className="col-sm-12 col-xs-12 col-md-3 col-lg-3" />
              <div className="col-sm-12 col-xs-12 col-md-9 col-lg-9">
                <Alert color="warning" className="mb-3 py-2 px-3 small">
                  <strong>
                    &quot;{dbAlias}&quot; is shared with {sharedDbAliasStores.length} other{' '}
                    {sharedDbAliasStores.length === 1 ? 'connection' : 'connections'} on this agent.
                  </strong>{' '}
                  They share one mapping configuration, so <em>Send Mappings</em> on any of them can clear the columns
                  this connection has added.{' '}
                  <Button
                    color="link"
                    className="p-0 align-baseline small"
                    aria-expanded={showSharedDbAlias}
                    onClick={() => setShowSharedDbAlias((shown) => !shown)}
                  >
                    {showSharedDbAlias ? 'Hide the list' : 'Show the list'}
                  </Button>
                  {showSharedDbAlias && (
                    <>
                      <ul
                        className="mt-2 mb-2"
                        style={{ columns: sharedDbAliasStores.length > 6 ? 2 : 1, columnGap: '2rem' }}
                      >
                        {sharedDbAliasStores.map((source) => (
                          <li key={source.id}>
                            {source.storeId === currentStore.storeId
                              ? 'another connection on this store'
                              : source.storeId}
                            {source.dbSchema ? ` \u2014 schema ${source.dbSchema}` : ''}
                          </li>
                        ))}
                      </ul>
                      Give each connection its own Database id, with a matching <code>[[Database]]</code> entry in the
                      agent&apos;s <code>sync_agent.toml</code>.
                    </>
                  )}
                </Alert>
              </div>
            </div>
          )}

          <SegmentItem name="Database schema">
            <DbSchemaSelect
              control={control}
              storeId={currentStore.storeId}
              protocol={protocol}
              hostName={hostName}
              port={port}
              dbAlias={dbAlias}
              onDbSchemaSelect={handleDbSchemaSelect}
            />
          </SegmentItem>
        </Segment>
      )}

      {message && (
        <div className="mt-4">
          <Alert color={failed ? 'danger' : 'success'} isOpen={!!message} toggle={onDismiss}>
            {message}
          </Alert>
        </div>
      )}

      {isSync2Mode() && (
        <div className="mt-4">
          <Button color="primary" disabled={isBusinessCentral()} onClick={handleSubmit(submit)}>
            Save
          </Button>

          {isSourceSelected() && !isGatewaySync() && (
            <Button color="primary" className="ms-4" onClick={handleTestConnection}>
              Test Connection
            </Button>
          )}
        </div>
      )}
    </form>
  );
}

export default SyncForm;
